import React, { useState, useEffect, useRef } from 'react';
import { taxaSet } from '../../static/bigVars/taxaSet';
import './search.css';
import { useTheme } from '../../theme/ThemeContext';

export default function Search({ placeholder, onSearch, disabled, suggestionsAllowed = false }) {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const { theme } = useTheme();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target) &&
                !inputRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputChange = (event) => {
        const value = event.target.value;
        setInputValue(value);

        if (value.length > 0) {
            const matchedSuggestions = Array.from(taxaSet)
                .filter(item => item.toLowerCase().includes(value.toLowerCase()))
                .sort((a, b) => a.length - b.length)
                .slice(0, 5);
            setSuggestions(matchedSuggestions);
            setShowSuggestions(suggestionsAllowed);
            setHighlightedIndex(-1);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
            setHighlightedIndex(-1);
        }
    };

    const handleKeyDown = (event) => {
        if (!showSuggestions || suggestions.length === 0) {
            if (event.key === 'Enter') {
                onSearch(inputValue);
                setShowSuggestions(false);
                setHighlightedIndex(-1);
            }
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex((prev) =>
                prev < suggestions.length - 1 ? prev + 1 : 0
            );
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((prev) =>
                prev > 0 ? prev - 1 : suggestions.length - 1
            );
        } else if (event.key === 'Enter') {
            if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                handleSuggestionClick(suggestions[highlightedIndex]);
            } else {
                onSearch(inputValue);
                setShowSuggestions(false);
                setHighlightedIndex(-1);
            }
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setInputValue(suggestion);
        onSearch(suggestion);
        setShowSuggestions(false);
        setHighlightedIndex(-1);
    };

    return (
        <div className="search-container">
            <input
                ref={inputRef}
                type="text"
                className="search-input"
                placeholder={placeholder || "Search..."}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                style={{ color: theme.colors.text }}
                autoComplete="off"
            />
            <button
                type="button"
                className="search-button"
                onClick={() => onSearch(inputValue)}
                disabled={disabled}
                style={{ color: theme.colors.text }}
            >
                Search
            </button>
            {showSuggestions && suggestions.length > 0 && (
                <ul ref={suggestionsRef} className="suggestions-list">
                    {suggestions.map((suggestion, index) => (
                        <li
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            style={{
                                backgroundColor:
                                    index === highlightedIndex
                                        ? theme.colors.overlay
                                        : 'inherit',
                                fontWeight: index === highlightedIndex ? 'bold' : 'normal',
                                cursor: 'pointer',
                            }}
                        >
                            {suggestion}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}